/* eslint-disable react/prop-types */
import { Toggle } from '@augmentt-dev/ui-components'
import React, { useEffect, useState } from 'react'
import { Button, Col, Form, Modal, Row, Tab, Tabs } from 'react-bootstrap'
import { FormattedMessage } from 'react-intl'
import PerfectScrollbar from 'react-perfect-scrollbar'
import Select, { createFilter } from 'react-select'
import { selectStyle } from '../../../../../../../_augmentt/utils/styles'
import { deleteEmployee } from '../../../../../../crud/employees.crud'
import { deleteEmployeeIntegrationInfo } from '../../../../../../crud/integration.crud'
import ScrollMenu from '../../../../../../partials/content/ScrollMenu'
import { getFinalValuesFromTaskParameterFunction, getTaskParameters } from '../../../config'
import { checkSelection, cloneChildComponents, formatEmployeeOptions, Option } from '../../../utils/helpers'

// Note: Remove User has 2 main tasks: the first being a pre-removal task, the second being the core remove user logic
function RemoveUser(props) {
  const { children, ...rest } = props
  const {
    intl,
    action,
    taskParameters,
    setTaskParameters,
    allLicensedEmployees,
    onActionSubmit,
    selectedEmployeeReference,
    setShowFindEmployeeModal,
    selectedEmployee,
    setDisableSubmit,
    setActionData,
    actionData,
    setCompletedActions,
    tasksCompleted,
    setLoadingData,
    loadingData,
    selectedCustomer,
    setSubmitButtonLangId,
    setActionHasAdditionalTasksToComplete,
  } = rest

  const [activeKey, setActiveKey] = useState('pre-removal') // pre-removal -> settings -> review

  // Pre-removal state
  const [isSuspended, setIsSuspended] = useState()
  const [isSuspending, setIsSuspending] = useState(
    // eslint-disable-next-line no-prototype-builtins
    actionData?.hasOwnProperty('suspended') ? actionData.suspended : true,
  )
  const [isResettingPassword, setResettingPassword] = useState(
    // eslint-disable-next-line no-prototype-builtins
    actionData?.hasOwnProperty('resetPassword') ? actionData.resetPassword : true,
  )
  const [isRevokingSession, setIsRevokingSession] = useState(
    // eslint-disable-next-line no-prototype-builtins
    actionData?.hasOwnProperty('revokeSessions') ? actionData.revokeSessions : true,
  )

  // Post-removal state
  const [transferToEmployee, setTransferToEmployee] = useState()
  const [employeeOptions, setEmployeeOptions] = useState([]) // Used in the employee selector in transfer data sub-action
  const [isTransferringData, setIsTransferringData] = useState(true)
  const [isDeletingEmployee, setIsDeletingEmployee] = useState(false) // Whether or not the user intends on deleting the employee from Augmentt
  const [isTransferringDriveAndDocs, setIsTransferringDriveAndDocs] = useState(true)
  const [isTransferringDriveAndDocsUnshared, setIsTransferringDriveAndDocsUnshared] = useState(false)
  const [isTransferringCalendar, setIsTransferringCalendar] = useState(true)
  const [isTransferringCalendarUserOrganized, setIsTransferringCalendarUserOrganized] = useState(false)
  const [isTransferringBrandAccounts, setIsTransferringBrandAccounts] = useState(true)
  const [isTransferringDataStudio, setIsTransferringDataStudio] = useState(true)
  const [isTransferringDataStudioUnshared, setIsTransferringDataStudioUnshared] = useState(false)

  // Handles initial render of component
  useEffect(() => {
    formatEmployeeOptions(allLicensedEmployees, intl, setEmployeeOptions)

    if (tasksCompleted === 1) {
      setSubmitButtonLangId('GENERAL.NEXT')
      setLoadingData({
        ...loadingData,
        loadingPhraseId: 'ENGAGE.OFFBOARDING_USER',
      })
      setActiveKey('settings')
      setActionHasAdditionalTasksToComplete(false) // This variable is used in IM onActionSubmit to determine whether to go into the success state or not after completion
      setDisableSubmit(true)
      setTaskParameters(getTaskParameters(action)) // Reset the task parameters back to the original function outlined in the config
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Handles updating actionData with the required task parameter data for pre-removal actions
  useEffect(() => {
    const newActionData = {}
    newActionData.revokeSessions = !isSuspended && isRevokingSession
    newActionData.suspended = !isSuspended && isSuspending
    newActionData.resetPassword = isResettingPassword

    setActionData(newActionData)
  }, [isSuspended, isRevokingSession, isResettingPassword, isSuspending]) // eslint-disable-line react-hooks/exhaustive-deps

  // Handles disabling/enabling the submit button during the transfer data task of remove user
  useEffect(() => {
    if (tasksCompleted === 1 && !isTransferringData) {
      setDisableSubmit(false)
    } else if (
      (tasksCompleted === 1 && isTransferringData && !transferToEmployee) ||
      (!isTransferringDriveAndDocs &&
        !isTransferringCalendar &&
        !isTransferringBrandAccounts &&
        !isTransferringDataStudio)
    ) {
      setDisableSubmit(true)
    } else {
      setDisableSubmit(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTransferringData,
    transferToEmployee,
    isTransferringDriveAndDocs,
    isTransferringCalendar,
    isTransferringBrandAccounts,
    isTransferringDataStudio,
  ])

  useEffect(() => {
    if (!isTransferringDataStudio) {
      setIsTransferringDataStudioUnshared(false)
    }
    if (!isTransferringDriveAndDocs) {
      setIsTransferringDriveAndDocsUnshared(false)
    }
    if (!isTransferringCalendar) {
      setIsTransferringCalendarUserOrganized(false)
    }
  }, [isTransferringDriveAndDocs, isTransferringDataStudio, isTransferringCalendar])

  // Handles firing off the task once the last required data field has been added to actionDatas
  useEffect(() => {
    // Submit the task if actionData has includeNotSharedCalender as a key. This is the final state setter and is used as a reference for when to fire off the task
    // eslint-disable-next-line no-prototype-builtins
    if (actionData?.hasOwnProperty('includeNotSharedCalender')) {
      // eslint-disable-next-line no-use-before-define
      submitTheTask()
    }
  }, [actionData]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sets whether or not the selected employee is suspended or not
  useEffect(() => {
    setIsSuspended(!selectedEmployee.enabled)
  }, [selectedEmployee])

  // Handles firing of the task
  const submitTheTask = () => {
    // Fire off task
    let finalTaskParameters = taskParameters

    // Form task parameter fields for taskParameters callback
    const taskParameterFields = {
      selectedEmployee,
      actionData,
    }

    // Task parameters should be a callback function here that was set in Data Loader
    if (typeof finalTaskParameters === 'function') {
      try {
        finalTaskParameters = getFinalValuesFromTaskParameterFunction(action, finalTaskParameters, taskParameterFields)
      } catch (error) {
        console.error(error)
      }
      setTaskParameters(finalTaskParameters)
    } else {
      console.error('Error - taskParameters should be a function')
    }

    // Ensure task parameters have all fields set. Set all unused fields to be false if they are unused
    if (tasksCompleted === 0) {
      finalTaskParameters.delete = false
      finalTaskParameters.dataTransfer = false
      finalTaskParameters.dataTransferOptions.newEmployeeIdentifier = ''
      finalTaskParameters.dataTransferOptions.calender.transfer = false
      finalTaskParameters.dataTransferOptions.calender.transfer = false
      finalTaskParameters.dataTransferOptions.calender.includeAllCalenderResources = false
      finalTaskParameters.dataTransferOptions.dataStudio.transfer = false
      finalTaskParameters.dataTransferOptions.dataStudio.includeNotShared = false
      finalTaskParameters.dataTransferOptions.drivesAndDocs.transfer = false
      finalTaskParameters.dataTransferOptions.drivesAndDocs.includeNotShared = false
      finalTaskParameters.dataTransferOptions.brandAccounts = false
    }

    // Submit the task
    onActionSubmit(
      action,
      finalTaskParameters,
      isDeletingEmployee
        ? deleteAugmenttEmployee // eslint-disable-line no-use-before-define
        : removeEmployeeIntegrationInfo, // eslint-disable-line no-use-before-define
    )
  }

  // Delete employee integration info if not being removed from augmentt
  async function removeEmployeeIntegrationInfo() {
    try {
      await deleteEmployeeIntegrationInfo('GWS', selectedCustomer?.id, selectedEmployee?.employeeId)
    } catch (e) {
      console.error(e.message)
    }
  }

  // Delete Augmentt employee
  async function deleteAugmenttEmployee() {
    try {
      await deleteEmployee(selectedEmployee?.employeeId)
    } catch (e) {
      console.error(e.message)
    }
  }

  // onSubmit callback for footer
  const next = () => {
    if (activeKey === 'pre-removal') {
      submitTheTask() // Fire off the pre-removal task, the component will be re-rendered and a useEffect will update the activeKey as needed
    } else if (activeKey === 'settings') {
      setActiveKey('review')
      setSubmitButtonLangId('ENGAGE.OFFBOARD_USER')
    } else {
      const newCompletedActions = []
      const newActionData = {}

      // Add the overall gws remove user action
      newCompletedActions.push('ENGAGE.GWS_USER_REMOVED_SUCCESS')

      // Check for all pre-removal actions that were completed
      if (actionData?.resetPassword) {
        newCompletedActions.push('ENGAGE.RESET_PASSWORD_SUCCESS')
      }
      if (actionData?.suspended) {
        newCompletedActions.push('ENGAGE.SUSPEND_SIGN_IN_SUCCESS')
      }
      if (actionData?.revokeSessions) {
        newCompletedActions.push('ENGAGE.SIGNED_OUT_ALL_APPS')
      }

      // Licenses attached to the employee that have been unassigned
      if (selectedEmployee?.licenseType.length) {
        newCompletedActions.push('ENGAGE.UNASSIGNED_SUCCESS')
        newActionData.licensesUnassigned = selectedEmployee?.licenseType
      }

      // Data ownership transfer action
      if (isTransferringData && transferToEmployee) {
        newCompletedActions.push('ENGAGE.TRANSFER_DATA_SUCCESS')
        newActionData.transferToEmployee = {
          employeeEmail: transferToEmployee?.email ? transferToEmployee.email : '',
        }
      }

      if (isDeletingEmployee) {
        newCompletedActions.push('ENGAGE.DELETING_EMPLOYEE_SUCCESS') // Also deleting the user from augmentt
      }

      // Set all action data needed for task parameters
      newActionData.revokeSessions = false
      newActionData.suspended = false
      newActionData.resetPassword = false
      newActionData.delete = true
      newActionData.transferData = isTransferringData // Whether the users data will be transferred to another employee
      newActionData.transferToEmployeeIdentifier = transferToEmployee?.value?.employeeIdentifier || null // The employee identifier of the employee taking ownership over the removed user's data
      newActionData.transferDrivesAndDocs = isTransferringDriveAndDocs
      newActionData.includeNotSharedDrives = isTransferringDataStudioUnshared
      newActionData.transferCalender = isTransferringCalendar
      newActionData.includeAllCalenderResources = isTransferringCalendarUserOrganized
      newActionData.transferBrandAccounts = isTransferringBrandAccounts
      newActionData.transferDataStudio = isTransferringDataStudio
      newActionData.includeNotSharedCalender = isTransferringDataStudioUnshared

      // Set the new completed actions and action data
      setCompletedActions((alreadyCompletedActions) => [...alreadyCompletedActions, ...newCompletedActions])
      setActionData({ ...actionData, ...newActionData })
      // Note: This will trigger the actionData useEffect to fire off the task to the integration manager
    }
  }

  return tasksCompleted === 0 ? (
    // UI for pre-removal task submission (regular modal-body)
    <PerfectScrollbar options={{ wheelSpeed: 2, wheelPropagation: false }} className="paginator-margin-bottom">
      <Form>
        <Modal.Body>
          <Row>
            <Col sm="12" md="12" lg="12">
              <p className="font-bolder font-lg mb-4">
                <FormattedMessage id="ENGAGE.PRE_OFFBOARDING_ACTIONS" />
              </p>

              <p className="subheader-p">
                <FormattedMessage id="ENGAGE.PRE_OFFBOARDING_ACTIONS_PHRASE" />
              </p>

              <div className="ms-4">
                <Form.Group id="pre-removal" className="mb-3">
                  {/* Reset Password Checkbox */}
                  <Form.Check
                    custom
                    type="checkbox"
                    id="resetPasswordCheckbox"
                    checked={isResettingPassword}
                    label={intl.formatMessage({
                      id: 'ENGAGE.RESET_PASSWORD',
                    })}
                    onClick={() => setResettingPassword(!isResettingPassword)}
                  />

                  {/* Suspend User Checkbox */}
                  <Form.Check
                    custom
                    type="checkbox"
                    id="suspendCheckbox"
                    checked={!isSuspended && isSuspending}
                    label={intl.formatMessage({
                      id: 'ENGAGE.SUSPEND_SING_IN_CHECKBOX_PHRASE',
                    })}
                    disabled={isSuspended}
                    onClick={() => setIsSuspending(!isSuspending)}
                  />

                  {/* Revoke Sessions Checkbox */}
                  <Form.Check
                    custom
                    type="checkbox"
                    id="revokeSessionsCheckbox"
                    checked={!isSuspended && isRevokingSession}
                    disabled={isSuspended}
                    label={intl.formatMessage({
                      id: 'ENGAGE.SIGN_OUT_OF_ALL_APPS',
                    })}
                    onClick={() => setIsRevokingSession(!isRevokingSession)}
                  />
                </Form.Group>
              </div>
            </Col>
          </Row>
        </Modal.Body>

        {/* Footer Child */}
        {cloneChildComponents({ ...rest, handleSubmit: next }, children)}
      </Form>
    </PerfectScrollbar>
  ) : (
    // UI for secondary task of user removal (multi-tab form)
    <Form>
      <Tabs activeKey={activeKey} onSelect={(key) => setActiveKey(key)} className="nav-fill engage-tabs">
        <Tab
          eventKey="settings"
          title={
            <p>
              <FormattedMessage id="GENERAL.SETTINGS" />
            </p>
          }
        >
          <div className="section">
            <Row className="justify-content-center align-items-center">
              <Row style={{ width: '100%' }}>
                <Col sm="12" md="12" lg="12">
                  <p className="font-bolder font-lg mb-4">
                    <FormattedMessage id="ENGAGE.TRANSFER_DATA" />
                  </p>
                  <div id="transferDataToggle" className="flex mb-2">
                    <p
                      className="pe-3"
                      style={{
                        color: isTransferringData ? 'var(--bs-body-color)' : 'var(--bs-secondary-color)',
                      }}
                    >
                      <FormattedMessage id="ENGAGE.TRANSFER_TO_ANOTHER_USER" />
                    </p>
                    <Toggle
                      onChange={() => setIsTransferringData(!isTransferringData)}
                      checked={!isTransferringData}
                      className="switch"
                      labelText=""
                      ariaLabel="transfer-data-toggle"
                    />
                    <p
                      className="ps-3"
                      style={{
                        color: !isTransferringData ? 'var(--bs-body-color)' : 'var(--bs-secondary-color)',
                      }}
                    >
                      <FormattedMessage id="ENGAGE.DONT_TRANSFER_DATA" />
                    </p>
                  </div>

                  {isTransferringData && (
                    <Select
                      id="employeeSelect"
                      ref={selectedEmployeeReference}
                      name="employee"
                      className="inline mb-4"
                      styles={selectStyle}
                      options={employeeOptions.filter((employee) => employee.email !== selectedEmployee?.email)}
                      onChange={(value) => {
                        checkSelection(value, setShowFindEmployeeModal, selectedEmployeeReference)
                        setTransferToEmployee(value)
                      }}
                      getOptionValue={(value) => value}
                      isClearable
                      placeholder={intl.formatMessage({
                        id: 'PLACEHOLDER.SELECT_EMPLOYEE',
                      })}
                      components={{
                        MenuList: ScrollMenu,
                        // eslint-disable-next-line react/no-unstable-nested-components, no-shadow
                        Option: (props) => <Option {...props} optionType="employee" />,
                      }}
                      filterOption={createFilter({
                        matchFrom: 'any',
                        stringify: (option) => `${option.label}`,
                      })}
                    />
                  )}

                  {!isTransferringData ? (
                    <p className="font-danger">
                      <FormattedMessage id="ENGAGE.TRANSFER_DELETE_WARNING" />
                    </p>
                  ) : (
                    <>
                      <p className="mb-4">
                        <FormattedMessage id="ENGAGE.SELECT_DATA_TO_TRANSFER" />
                      </p>

                      {/* Select data to transfer checkboxes */}
                      <Form.Group id="selectDataToTransfer" className="ms-4">
                        {/* Drive and Docs */}
                        <Form.Check
                          custom
                          type="checkbox"
                          id="drivesAndDocsCheckbox"
                          checked={isTransferringDriveAndDocs}
                          label={intl.formatMessage({
                            id: 'ENGAGE.DRIVES_AND_DOCS',
                          })}
                          onClick={() => setIsTransferringDriveAndDocs(!isTransferringDriveAndDocs)}
                        />
                        <Form.Check
                          custom
                          className="ms-4"
                          type="checkbox"
                          disabled={!isTransferringDriveAndDocs}
                          id="drivesAndDocsUnsharedCheckbox"
                          checked={isTransferringDriveAndDocsUnshared}
                          label={
                            <div className="font-color-6">
                              {intl.formatMessage({
                                id: 'ENGAGE.DRIVES_AND_DOCS_UNSHARED',
                              })}
                            </div>
                          }
                          onClick={() => setIsTransferringDriveAndDocsUnshared(!isTransferringDriveAndDocsUnshared)}
                        />

                        {/* Calendar */}
                        <Form.Check
                          custom
                          type="checkbox"
                          id="calendarCheckbox"
                          checked={isTransferringCalendar}
                          label={intl.formatMessage({
                            id: 'ENGAGE.CALENDAR',
                          })}
                          onClick={() => setIsTransferringCalendar(!isTransferringCalendar)}
                        />
                        <Form.Check
                          custom
                          className="font-color-6 ms-4"
                          disabled={!isTransferringCalendar}
                          type="checkbox"
                          id="calendarSubCheckbox"
                          checked={isTransferringCalendarUserOrganized}
                          label={
                            <div className="font-color-6">
                              {intl.formatMessage({
                                id: 'ENGAGE.CALENDAR_USER_ORGANIZED',
                              })}
                            </div>
                          }
                          onClick={() => setIsTransferringCalendarUserOrganized(!isTransferringCalendarUserOrganized)}
                        />

                        {/* Brand accounts */}
                        <Form.Check
                          custom
                          type="checkbox"
                          id="brandAccountsCheckbox"
                          checked={isTransferringBrandAccounts}
                          label={
                            <>
                              <span>
                                {intl.formatMessage({
                                  id: 'ENGAGE.BRAND_ACCOUNTS',
                                })}
                              </span>
                              <p className="font-color-6" style={{ marginBottom: '0px' }}>
                                <FormattedMessage id="ENGAGE.BRAND_ACCOUNTS_SUB_PHRASE" />
                              </p>
                            </>
                          }
                          onClick={() => setIsTransferringBrandAccounts(!isTransferringBrandAccounts)}
                        />

                        {/* Data Studio */}
                        <Form.Check
                          custom
                          type="checkbox"
                          id="dataStudioCheckbox"
                          checked={isTransferringDataStudio}
                          label={intl.formatMessage({
                            id: 'ENGAGE.DATA_STUDIO',
                          })}
                          onClick={() => setIsTransferringDataStudio(!isTransferringDataStudio)}
                        />
                        <Form.Check
                          custom
                          className="ms-4 font-color-6"
                          type="checkbox"
                          id="dataStudioUnsharedCheckbox"
                          disabled={!isTransferringDataStudio}
                          checked={isTransferringDataStudioUnshared}
                          label={
                            <div className="font-color-6">
                              {intl.formatMessage({
                                id: 'ENGAGE.DATA_STUDIO_UNSHARED',
                              })}
                            </div>
                          }
                          onClick={() => setIsTransferringDataStudioUnshared(!isTransferringDataStudioUnshared)}
                        />
                      </Form.Group>
                    </>
                  )}

                  <p className="font-bolder font-lg mb-2 mt-4">
                    <FormattedMessage id="ENGAGE.DELETE_EMPLOYEE_FROM_AUGMENTT" />
                  </p>

                  {/* Delete employee checkbox */}
                  <Form.Group id="deleteEmployee" className="ms-4">
                    <Form.Check
                      custom
                      type="checkbox"
                      id="deleteEmployeeCheckbox"
                      checked={isDeletingEmployee}
                      label={
                        <>
                          <span>
                            {intl.formatMessage({
                              id: 'ENGAGE.DELETE_EMPLOYEE_FROM_AUGMENTT_CHECK',
                            })}
                          </span>
                          <p className="font-color-6" style={{ marginBottom: '0px' }}>
                            <FormattedMessage id="ENGAGE.DELETE_AUGMENTT_FROM_AUGMENTT_CONFIRMATION" />
                          </p>
                        </>
                      }
                      onClick={() => setIsDeletingEmployee(!isDeletingEmployee)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Row>
          </div>
        </Tab>
        <Tab
          eventKey="review"
          title={
            <p>
              <FormattedMessage id="ENGAGE.REVIEW_AND_FINISH" />
            </p>
          }
          disabled={activeKey !== 'review'}
        >
          <Row>
            <Col sm="12" md="12" lg="12">
              {isTransferringData ? (
                <>
                  <p className="font-bolder font-lg mb-1">
                    <FormattedMessage id="ENGAGE.TRANSFER_DATA_OWNERSHIP" />
                  </p>
                  <p className="mb-1">
                    {selectedEmployee ? selectedEmployee.firstName + selectedEmployee.lastName : ''}
                  </p>
                </>
              ) : (
                <p className="font-bolder font-lg mb-1">
                  <FormattedMessage id="ENGAGE.DELETING_DATA" />
                </p>
              )}

              {isDeletingEmployee && (
                <p className="font-bolder font-lg mb-1 mt-1">
                  <FormattedMessage id="ENGAGE.DELETING_EMPLOYEE" />
                </p>
              )}

              <Button
                variant="link"
                className="mb-4 ps-0 pe-0"
                onClick={() => {
                  setActiveKey('settings')
                }}
              >
                <span>
                  <FormattedMessage id="GENERAL.EDIT" />
                </span>
              </Button>
            </Col>
          </Row>
        </Tab>
      </Tabs>

      {/* Footer Child */}
      {cloneChildComponents({ ...rest, handleSubmit: next }, children)}
    </Form>
  )
}

export default RemoveUser
